/**
 * HandoffErrorPage
 * The page every dead end in the handoff flow lands on.
 *
 * Classification of the `reason` param lives in utils/handoffErrorCopy so it can
 * be tested without React; this file only picks the icon for each kind and keeps
 * the "someone beat you to it" GIF on its own branch.
 */

import React from 'react';
import {
  AlertCircle,
  Flame,
  Hourglass,
  Lock,
  RotateCcw,
  SearchX,
  Unplug,
  UserCheck,
  Users,
  XCircle,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  HandoffAction,
  HandoffOutcome,
  HandoffPageShell,
} from '../../components/features/handoff/HandoffPageShell';
import { HandoffErrorKind, resolveHandoffError } from '../../utils/handoffErrorCopy';

const KIND_ICONS: Record<HandoffErrorKind, React.ReactNode> = {
  held: <Users />,
  claimed: <UserCheck />,
  released: <RotateCcw />,
  declined: <XCircle />,
  not_open: <Lock />,
  stale: <Hourglass />,
  taken_back: <Hourglass />,
  scrambled: <Unplug />,
  invalid_action: <Unplug />,
  not_found: <SearchX />,
  generic: <AlertCircle />,
};

const HandoffErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');

  const copy = resolveHandoffError(reason);

  const actions: HandoffAction[] = [
    {
      label: 'See other hot leads',
      onClick: () => navigate('/dashboard?openHandoffQueue=true'),
      icon: <Flame className="mr-2 h-4 w-4" />,
      variant: 'primary',
    },
    {
      label: 'Go to contacts',
      onClick: () => navigate('/contacts'),
      variant: 'outline',
    },
  ];

  return (
    <HandoffPageShell>
      <HandoffOutcome
        tone={copy.tone}
        icon={KIND_ICONS[copy.kind]}
        title={copy.title}
        message={copy.message}
        actions={actions}
        media={
          copy.kind === 'held' ? (
            <img
              src="/assets/images/you-got-to-be-quicker-than-that-gotta-be-quicker-than-that.gif"
              alt="You gotta be quicker than that!"
              className="h-auto w-full"
            />
          ) : undefined
        }
      />
    </HandoffPageShell>
  );
};

export default HandoffErrorPage;
